import { Button, Grid, Group, Tabs } from '@mantine/core';
import {
  IconChevronLeft,
  IconChevronRight,
  IconCopy,
  IconDeviceFloppy,
  IconLayoutDashboard,
  IconPalette,
  IconSettings,
  IconTrash,
  IconUsers,
} from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  CHROME_CSS_STYLE_KEYS,
  cardControlsOffered,
  cardOffsetError,
  cardSizeError,
  pruneUnreachableCardCssStyles,
} from '../../constants/bannerChrome';
import { isRecommenderPageLimitError, showRecommenderPageLimitNotification } from '../../helpers/recommenderPageLimits';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import LockableButton from '../Lockable/LockableButton';
import ConfirmationModal from '../Modals/ConfirmationModal';
import PageBlockAppearance from '../PageBlockAppearance';
import PageBlockDisplayConfiguration from '../PageBlockDisplayConfiguration';
import PositionOnMySite from '../PositionOnMySite';
import usePreview from '../Templates/usePreview';
import useTemplate from '../Templates/useTemplate';
import RecommenderCopyModal from './RecommenderCopyModal';
import AudienceRecommenderForm from './audienceRecommenderForm';
import GeneralRecommenderForm from './generalRecommenderForm';

const RecommenderForm = ({
  recommender: passedRecommender,
  deleteRecommender,
  cancel,
  allowSegmentSelection,
  initialTab,
  onSaved,
}) => {
  const { accountId, domainId } = useParams();
  const tabs = ['general', 'audience', 'displayConfiguration', 'appearance'];
  const navigate = useNavigate();
  const fetch = useFetch();
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [userError, setUserError] = useState();
  const [loadError, setLoadError] = useState();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [recommender, setRecommender] = useState(passedRecommender);
  // The page opens the form on display configuration when it built this
  // block from a positioning session, so the merchant lands looking at the
  // position they came back to review.
  const [tab, setTab] = useState(initialTab || tabs[0]);
  const template = useTemplate(accountId, domainId, recommender.templateId, templates);
  const preview = usePreview(template);
  const [translations, setTranslations] = useState(
    Array.isArray(recommender.titleTranslations) ? recommender.titleTranslations : []
  );
  const [saving, setSaving] = useState(false);
  const apiBaseUrl = `/api/admin/accounts/${accountId}/domains/${domainId}`;
  const refreshTemplates = useCallback(
    (cb) => {
      fetch(`${apiBaseUrl}/templates`)
        .then(({ templates }) => {
          setTemplates(templates.filter((t) => t.type === 'recommender' && t.scope === 'web'));
          if (typeof cb === 'function') {
            cb();
          }
        })
        .catch(setLoadError);
    },
    [apiBaseUrl, fetch]
  );

  useEffect(() => {
    refreshTemplates();
  }, [refreshTemplates]);

  useEffect(() => {
    if (recommender?.urlFilter === false) {
      setRecommender((recommender) => ({ ...recommender, urlRegex: '', urlRegexNoMatch: '' }));
    }
  }, [recommender?.urlFilter]);

  // A page limit the API refuses to let this save cross is reported as a
  // notification rather than through the in-form banner: it is about the page
  // the block sits on, not about a field the author can correct here.
  const handleSaveError = (err) => {
    if (isRecommenderPageLimitError(err)) {
      showRecommenderPageLimitNotification(err);
    } else {
      setUserError(err);
    }
    setSaving(false);
  };

  const createRecommender = (onSuccess, recommenderOverride) => {
    const rec = recommenderOverride || recommender;
    fetch(`${apiBaseUrl}/pages/${rec.pageId}/recommenders`, {
      method: 'POST',
      body: JSON.stringify({
        ...rec,
        templateId: rec.templateId || null,
        running: false,
        // See `updateRecommender` below for why this has to happen at save
        // time rather than never.
        cssStyles: pruneUnreachableCardCssStyles(rec.cssStyles, rec),
      }),
    })
      .then(({ recommender: savedRecommender }) => {
        onSuccess(savedRecommender);
      })
      .catch(handleSaveError);
  };

  const updateRecommender = (onSuccess) => {
    fetch(`${apiBaseUrl}/pages/${recommender.pageId}/recommenders/${recommender.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        ...recommender,
        templateId: recommender.templateId || null,
        // A `cardWidth`/`cardHeight` typed while its control was on screen
        // outlives the control (Advanced styling switched off, or the
        // display type no longer offers it — `bar` for `cardWidth`
        // specifically). `isInvalid` below does not gate either Save button
        // on a stale value the author can no longer see or edit, so without
        // this it would still ride along in the payload and the API would
        // 400 on it with nothing on the form to point back to.
        cssStyles: pruneUnreachableCardCssStyles(recommender.cssStyles, recommender),
      }),
    })
      .then(({ recommender: savedRecommender }) => {
        // Reported here, where the PUT lands, rather than from each button:
        // what the page does with it — retiring a positioning session whose
        // stored snapshot this save has just superseded — is true of any
        // landed update, and wiring it per button made it true of `save` and
        // not of `saveAndClose`. Before `onSuccess` because `saveAndClose`
        // navigates: the page strips the parameter with a `replace`, which
        // has to land on this entry rather than on the one pushed next.
        onSaved?.();
        onSuccess(savedRecommender);
      })
      .catch(handleSaveError);
  };

  const create = (shouldClose = true, recommenderOverride) => {
    if (!shouldClose) setSaving(true);

    createRecommender((savedRecommender) => {
      if (shouldClose) {
        navigate(`/accounts/${accountId}/domains/${domainId}/pages?type=recommenders&page=${savedRecommender.pageId}`);
      } else {
        setRecommender(savedRecommender);
        setSaving(false);
      }
    }, recommenderOverride);
  };

  const save = () => {
    setSaving(true);
    updateRecommender((savedRecommender) => {
      setRecommender(savedRecommender);
      setSaving(false);
      setUserError(undefined);
    });
  };

  const saveAndClose = () => {
    updateRecommender(() => {
      navigate(`/accounts/${accountId}/domains/${domainId}/pages?type=recommenders&page=${recommender.pageId}`);
    });
  };
  const addTranslation = useCallback(() => {
    const newTranslation = { locale: {}, translation: '' };
    setTranslations((prev) => {
      const newTranslations = [...prev, newTranslation];
      setRecommender((rec) => ({ ...rec, titleTranslations: newTranslations }));
      return newTranslations;
    });
  }, []);

  const removeTranslation = useCallback((index) => {
    setTranslations((prev) => {
      const newTranslations = prev.filter((_, i) => i !== index);
      setRecommender((rec) => ({ ...rec, titleTranslations: newTranslations }));
      return newTranslations;
    });
  }, []);

  const handleTranslationChange = useCallback((index, field, value) => {
    setTranslations((prev) => {
      const newTranslations = [...prev];
      newTranslations[index][field] = value;
      setRecommender((rec) => ({ ...rec, titleTranslations: newTranslations }));
      return newTranslations;
    });
  }, []);

  // Updated from the previous state rather than from the `recommender` this
  // render closed over, because one gesture on the advanced-styling panel's
  // card placement picker can produce two `cssStyles` writes (one per axis,
  // for a pop-up or flyout), batched before any re-render — read from the
  // closure, the second call would be computed from the pre-gesture block and
  // drop the first. Mirrors `BannerBlockForm.handleStateChange`.
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    if (name === 'urlFilter' && value === false) {
      setRecommender((prev) => ({ ...prev, urlRegex: '', urlRegexNoMatch: '' }));
    } else if (CHROME_CSS_STYLE_KEYS.has(name)) {
      // A chrome field (including `customCss`) names the `cssStyles` key it
      // edits, not a column on the recommender — and turns `advancedStyling`
      // on, which is what lets the styling reach the shop: the API serves
      // `advancedStyling === false` the defaults and drops the stored
      // `cssStyles` (`helpers/resolve-popup-css-styles.js`). See
      // `BannerBlockForm.handleStateChange` for the whole reasoning.
      setRecommender((prev) => ({
        ...prev,
        cssStyles: { ...prev.cssStyles, [name]: value },
        advancedStyling: true,
      }));
    } else {
      setRecommender((prev) => ({ ...prev, [name]: value }));
    }
  }, []);

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (recommender === undefined) {
    return null;
  }

  // Same panel as the banner block form, so it is scoped the same way:
  // `PageBlockAdvancedStyling` only mounts with the switch on for a modal
  // display type, and hides the card-width/offset-horizontal row for `bar`.
  // A stale value left over from before the switch was turned off, or from
  // before the display type changed to `bar`, has no control left to fix it
  // through, so it must not surface as an unexplained "invalid value" banner.
  const cardControlsOnScreen = cardControlsOffered(recommender.displayType);
  const isInvalid =
    !recommender.name ||
    !recommender.productFeedId ||
    (allowSegmentSelection && !recommender.segmentId) ||
    (!recommender.cssSelector && recommender.templateId && recommender.displayType === 'static') ||
    (cardControlsOnScreen && recommender.displayType !== 'bar' && !!cardSizeError(recommender.cssStyles?.cardWidth)) ||
    (cardControlsOnScreen && !!cardSizeError(recommender.cssStyles?.cardHeight)) ||
    (cardControlsOnScreen &&
      recommender.displayType !== 'bar' &&
      !!cardOffsetError(recommender.cssStyles?.cardOffsetHorizontal)) ||
    (cardControlsOnScreen && !!cardOffsetError(recommender.cssStyles?.cardOffsetVertical));

  // A new recommender used to be saveable only from the last tab, which was the
  // display configuration. Appearance now sits after it and is optional — a
  // block with no chrome styled saves perfectly well — so the save buttons are
  // offered from the display configuration on, with Next still there for an
  // author who does want to style it. One that already exists keeps them on
  // every tab, as it did. The missing-fields notice follows the buttons it
  // explains.
  const saveOffered = !!recommender.id || tabs.indexOf(tab) >= tabs.indexOf('displayConfiguration');
  const nextOffered = !recommender.id && tabs.indexOf(tab) < tabs.length - 1;

  const closeCopyModal = () => {
    if (copyModalOpen) {
      setCopyModalOpen(false);
    }
  };

  let deleteButton;
  if (typeof deleteRecommender === 'function') {
    deleteButton = (
      <LockableButton restrictionKey="productBlocksDelete" color="red" onClick={() => setConfirmDeleteOpen(true)}>
        <IconTrash size={14} /> Delete
      </LockableButton>
    );
  }

  let copyButton;
  if (typeof deleteRecommender === 'function') {
    copyButton = (
      <LockableButton restrictionKey="productBlocksCreate" color="cyan" onClick={() => setCopyModalOpen(true)}>
        <IconCopy size={14} /> Make a copy
      </LockableButton>
    );
  }

  return (
    <Grid.Col span={{ base: 12, md: 12 }}>
      <form>
        <Tabs value={tab} onChange={setTab} mb="md" keepMounted>
          <Tabs.List>
            <Tabs.Tab value="general" leftSection={<IconSettings size={16} />}>
              General
            </Tabs.Tab>
            <Tabs.Tab value="audience" leftSection={<IconUsers size={16} />} data-tour="recommenderAudienceTab">
              Audience
            </Tabs.Tab>
            <Tabs.Tab
              value="displayConfiguration"
              leftSection={<IconLayoutDashboard size={16} />}
              data-tour="recommenderDisplayConfigurationTab"
            >
              Display Configuration
            </Tabs.Tab>
            <Tabs.Tab value="appearance" leftSection={<IconPalette size={16} />} data-tour="recommenderAppearanceTab">
              Appearance
            </Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="general" pt={0}>
            <GeneralRecommenderForm
              accountId={accountId}
              domainId={domainId}
              recommender={recommender}
              handleChange={handleChange}
              setRecommender={setRecommender}
              setUserError={setUserError}
              handleTranslationChange={handleTranslationChange}
              translations={translations}
              addTranslation={addTranslation}
              removeTranslation={removeTranslation}
            />
          </Tabs.Panel>
          <Tabs.Panel value="audience">
            {allowSegmentSelection && (
              <AudienceRecommenderForm
                recommender={recommender}
                handleChange={handleChange}
                setRecommender={setRecommender}
                scope="transactional"
              />
            )}
          </Tabs.Panel>
          <Tabs.Panel value="displayConfiguration" pt={0}>
            {tab === 'displayConfiguration' && (
              <PageBlockDisplayConfiguration
                pageBlock={recommender}
                templates={templates}
                template={template}
                refreshTemplates={refreshTemplates}
                handleChange={handleChange}
              />
            )}
          </Tabs.Panel>
          <Tabs.Panel value="appearance" pt={0}>
            {tab === 'appearance' && (
              <PageBlockAppearance
                pageBlock={recommender}
                preview={preview}
                handleChange={handleChange}
                // The selector row moved to this tab with the rest of "where
                // the block goes"; it is only rendered for a static block.
                positionOnSite={(cssSelectorField) => (
                  <PositionOnMySite block={recommender} blocksPath="recommenders" blockLabel="product block">
                    {cssSelectorField}
                  </PositionOnMySite>
                )}
              />
            )}
          </Tabs.Panel>
        </Tabs>
        <Group justify="flex-end" gap="xs" py="md">
          {recommender.id || tabs.indexOf(tab) === 0 ? (
            <Button variant="default" onClick={() => cancel()}>
              <IconChevronLeft /> Back
            </Button>
          ) : (
            <Button variant="default" onClick={() => setTab(tabs[tabs.indexOf(tab) - 1])}>
              <IconChevronLeft /> Back
            </Button>
          )}
          {deleteButton}
          {copyButton}
          {nextOffered && (
            <Button
              onClick={() => {
                setTab(tabs[tabs.indexOf(tab) + 1]);
                window.scrollTo(0, 0);
              }}
            >
              Next <IconChevronRight size={14} />
            </Button>
          )}
          {saveOffered && (
            <>
              {recommender.id && (
                <LockableButton
                  restrictionKey="productBlocksUpdate"
                  variant="light"
                  onClick={save}
                  disabled={saving}
                  loading={saving}
                >
                  <IconDeviceFloppy size={14} /> Save
                </LockableButton>
              )}
              <LockableButton
                restrictionKey={`productBlocks${recommender.id ? 'Update' : 'Create'}`}
                onClick={() => (recommender?.id ? saveAndClose() : create())}
                data-tour="saveBtnRecommender"
              >
                <IconDeviceFloppy size={14} /> Save & Close
              </LockableButton>
            </>
          )}
        </Group>
        {saveOffered && isInvalid && (
          <div>
            <ErrorMessage message="Some required fields are missing or have an invalid value." />
          </div>
        )}
      </form>
      <RecommenderCopyModal
        recommender={recommender}
        show={copyModalOpen}
        save={(recommender) => {
          setRecommender(recommender);
          create(true, recommender);
        }}
        close={closeCopyModal}
      />
      <ConfirmationModal
        show={confirmDeleteOpen}
        close={() => setConfirmDeleteOpen(false)}
        confirm={(cb) => deleteRecommender(cb)}
        message="This action cannot be undone!"
      />
      {userError && (
        <div>
          <ErrorMessage message={userError.message} />
        </div>
      )}
    </Grid.Col>
  );
};

export default RecommenderForm;
